import { Ionicons } from '@expo/vector-icons';
import { Picker } from '@react-native-picker/picker';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  I18nManager,
  Modal,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { userApi } from '../services/api';
import logger from '../utils/logger';
import { canManageUsers, isOrganizationAdmin, isPlatformAdmin } from '../utils/permissions';

interface Role {
  name: string;
  nameAr?: string;
  scope: string;
}

interface Organization {
  id: string;
  name: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  orgId: string;
  roles: Role[];
  organization: Organization | null;
  createdAt: string;
}

export const UsersScreen = () => {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { user: currentUser } = useAuth();
  const navigation = useNavigation();
  const isRTL = I18nManager.isRTL || i18n.language === 'ar';
  const isArabic = i18n.language === 'ar';
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [availableRoles, setAvailableRoles] = useState<
    { id: string; name: string; nameAr?: string; scope: string }[]
  >([]);
  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role: string; // holds either a role ID or legacy name like 'admin'|'operator'|'viewer'
  }>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: '',
  });

  // Check if current user is admin (includes both org admins and platform admins)
  const isAdmin = currentUser?.roles
    ? isPlatformAdmin(currentUser.roles) || isOrganizationAdmin(currentUser.roles)
    : false;
  const hasPermission = currentUser?.roles ? canManageUsers(currentUser.roles) : false;

  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? 'إدارة المستخدمين' : 'Users Management',
      headerTintColor: '#ffffff',
      headerTitleStyle: { color: '#ffffff', fontWeight: '700' },
    });
    if (hasPermission && isAdmin) {
      loadUsers();
      loadRoles();
    } else if (!hasPermission && !isAdmin && currentUser !== undefined) {
      // Only redirect if user data is loaded but permission is denied
      Alert.alert(
        isArabic ? 'صلاحية مطلوبة' : 'Permission Required',
        isArabic
          ? 'لا تملك صلاحية للوصول لهذه الصفحة'
          : 'You do not have permission to access this page.',
        [{ text: isArabic ? 'حسناً' : 'OK', onPress: () => navigation.goBack() }],
      );
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, hasPermission, isArabic]);

  const loadRoles = async () => {
    try {
      const roles = await userApi.getRoles();
      if (Array.isArray(roles)) {
        setAvailableRoles(roles);
        logger.log('Roles loaded:', roles.length);
      }
    } catch (error) {
      logger.error('Error loading roles:', error);
    }
  };

  // Map a role name to its ID from availableRoles.
  // Accepts either: an exact role name from DB (e.g. 'مدير النظام'), a UUID, or a legacy English key.
  const getRoleId = (roleName: string): string | null => {
    if (!roleName) return null;
    // If it's already a UUID, return as-is (caller should check that first)
    if (/^[0-9a-f-]{36}$/i.test(roleName)) return roleName;

    // Try exact name match first
    const exact = availableRoles.find((r) => r.name === roleName);
    if (exact) return exact.id;

    const lower = roleName.toLowerCase();

    // Legacy English key fallbacks
    if (lower === 'admin') {
      const found = availableRoles.find((r) => r.scope === 'organization');
      return found?.id ?? null;
    }
    if (lower === 'operator' || lower === 'technician') {
      const found = availableRoles.find((r) => r.name === 'فني صيانة');
      return found?.id ?? null;
    }
    if (lower === 'supervisor') {
      const found = availableRoles.find((r) => r.name === 'مشرف قسم' || r.name === 'مدير المستشفى');
      return found?.id ?? null;
    }
    // 'viewer' had no matching DB role — fall back to lowest zone role
    if (lower === 'viewer') {
      const found = availableRoles.find((r) => r.scope === 'zone');
      return found?.id ?? null;
    }

    // Last resort: partial name search
    const partial = availableRoles.find((r) => r.name.toLowerCase().includes(lower));
    return partial?.id ?? null;
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await userApi.getUsers();
      logger.log('Users loaded:', usersData);
      setUsers(usersData);
    } catch (error) {
      logger.error('Error loading users:', error);
      Alert.alert(t('common.error'), t('errors.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadUsers();
    setRefreshing(false);
  };

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: availableRoles.length > 0 ? availableRoles[0].id : '',
    });
    setModalVisible(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    // prefer the role ID from availableRoles if we can match by name, else fall back to first available
    const userRoleName = user.roles && user.roles[0] ? user.roles[0].name : '';
    const matchedRole = availableRoles.find((r) => r.name === userRoleName);
    const roleValue = matchedRole?.id || (availableRoles.length > 0 ? availableRoles[0].id : '');
    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      password: '',
      role: roleValue,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      Alert.alert(
        t('common.error'),
        isArabic
          ? 'الاسم الأول والأخير والبريد الإلكتروني مطلوبة'
          : 'First name, last name and email are required',
      );
      return;
    }

    if (!editingUser && !formData.password.trim()) {
      Alert.alert(t('common.error'), isArabic ? 'كلمة المرور مطلوبة' : 'Password is required');
      return;
    }

    if (!currentUser?.orgId) {
      Alert.alert(
        t('common.error'),
        isArabic ? 'لم يتم العثور على معرّف المنظمة' : 'Organization ID not found',
      );
      return;
    }

    // Resolve roleId: if formData.role looks like a UUID use it directly, otherwise fall back to name-based lookup
    const isUuid = /^[0-9a-f-]{36}$/i.test(formData.role);
    const roleId = isUuid ? formData.role : getRoleId(formData.role);
    if (!roleId) {
      logger.warn(
        `Role not found for: ${formData.role}. Available:`,
        availableRoles.map((r) => `${r.name}(${r.id})`),
      );
    }
    const roleIds = roleId ? [roleId] : [];

    try {
      if (editingUser) {
        const updatePayload: {
          email?: string;
          password?: string;
          roleIds?: string[];
          firstName?: string;
          lastName?: string;
        } = {
          email: formData.email,
          roleIds,
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
        };
        if (formData.password.trim()) {
          updatePayload.password = formData.password;
        }
        await userApi.updateUser(editingUser.id, updatePayload);
        setModalVisible(false);
        loadUsers();
        Alert.alert(
          t('common.success'),
          isArabic ? 'تم تحديث المستخدم بنجاح' : 'User updated successfully',
        );
      } else {
        await userApi.createUser({
          email: formData.email,
          password: formData.password,
          orgId: currentUser.orgId,
          roleIds,
          firstName: formData.firstName,
          lastName: formData.lastName,
        });
        setModalVisible(false);
        loadUsers();

        // BUG-010: Never display the password in plain text in a dialog
        const userInfo = isArabic
          ? `الاسم: ${formData.firstName} ${formData.lastName}\nالبريد: ${formData.email}\nالدور: ${formData.role}`
          : `Name: ${formData.firstName} ${formData.lastName}\nEmail: ${formData.email}\nRole: ${formData.role}`;

        Alert.alert(
          isArabic ? 'تم إنشاء المستخدم بنجاح ✅' : 'User Created Successfully ✅',
          userInfo,
          [{ text: isArabic ? 'موافق' : 'OK', style: 'default' }],
        );
      }
    } catch (error: any) {
      logger.error('Error saving user:', error);
      const msg = error?.message || (isArabic ? 'حدث خطأ في الخادم' : 'Server error');
      Alert.alert(t('common.error'), msg);
    }
  };

  const handleDelete = (user: User) => {
    if (user.id === currentUser?.id) {
      Alert.alert(t('common.error'), 'Cannot delete yourself');
      return;
    }

    Alert.alert(t('users.deleteUser'), t('users.deleteConfirm'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'),
        style: 'destructive',
        onPress: async () => {
          try {
            await userApi.deleteUser(user.id);
            loadUsers();
            Alert.alert(t('common.success'), t('common.success'));
          } catch (error) {
            Alert.alert(t('common.error'), t('errors.serverError'));
          }
        },
      },
    ]);
  };

  // Returns a display category based on actual DB role.
  // Categories: 'platform' | 'admin' | 'supervisor' | 'operator' | 'unknown'
  const getUserRole = (user: User): string => {
    if (!user.roles || user.roles.length === 0) return 'unknown';
    const role = user.roles[0];
    const name = role.name;
    const scope = role.scope;
    if (scope === 'platform' || name === 'مدير المنصة') return 'platform';
    if (scope === 'organization') return 'admin'; // مدير النظام, Demo Admin
    // Zone-level roles
    if (name === 'مدير المستشفى' || name === 'مشرف قسم') return 'supervisor';
    if (name === 'فني صيانة' || scope === 'zone') return 'operator';
    return 'unknown';
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'platform':
        return '#DC2626'; // red — platform admin
      case 'admin':
        return theme.colors.error; // org admin
      case 'supervisor':
        return '#7B1FA2'; // purple — zone manager/supervisor
      case 'operator':
        return '#F59E0B'; // amber — technician
      default:
        return theme.colors.info;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'platform':
        return isArabic ? 'مدير المنصة' : 'Platform Admin';
      case 'admin':
        return t('users.admin');
      case 'supervisor':
        return isArabic ? 'مشرف / مدير' : 'Supervisor';
      case 'operator':
        return isArabic ? 'فني صيانة' : 'Technician';
      default:
        return isArabic ? 'مستخدم' : 'User';
    }
  };

  const renderUserCard = ({ item }: { item: User }) => {
    const userRole = getUserRole(item);
    const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email;
    const firstLetter =
      item.firstName && item.firstName.length > 0
        ? item.firstName.charAt(0).toUpperCase()
        : item.email && item.email.length > 0
        ? item.email.charAt(0).toUpperCase()
        : '?';
    const orgName = item.organization?.name || (isArabic ? 'بدون مؤسسة' : 'No Organization');

    return (
      <TouchableOpacity
        style={[
          styles.card,
          { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
        ]}
        onPress={() => isAdmin && handleEdit(item)}
        onLongPress={() => isAdmin && handleDelete(item)}
        disabled={!isAdmin}
        accessible={true}
        accessibilityLabel={`${isArabic ? 'المستخدم' : 'User'}: ${fullName}, ${
          isArabic ? 'البريد' : 'Email'
        }: ${item.email}, ${isArabic ? 'الدور' : 'Role'}: ${getRoleLabel(userRole)}`}
        accessibilityHint={
          isAdmin
            ? isArabic
              ? 'انقر نقرًا مزدوجًا للتعديل، اضغط مطولاً للحذف'
              : 'Double tap to edit, long press to delete'
            : isArabic
            ? 'عرض فقط - لا توجد صلاحيات للتعديل'
            : 'View only - no edit permissions'
        }
        accessibilityRole="button"
        accessibilityState={{ disabled: !isAdmin }}
      >
        <View style={[styles.cardHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
          <View
            style={[
              styles.avatar,
              {
                backgroundColor: getRoleColor(userRole),
                marginStart: isRTL ? 12 : 0,
                marginEnd: isRTL ? 0 : 12,
              },
            ]}
          >
            <Text style={styles.avatarText}>{firstLetter}</Text>
          </View>
          <View style={[styles.userInfo, { alignItems: 'flex-start' }]}>
            <Text
              style={[
                styles.userName,
                { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
              ]}
            >
              {fullName}
            </Text>
            <Text
              style={[
                styles.userEmail,
                { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
              ]}
            >
              {item.email}
            </Text>
            <Text
              style={[
                styles.userPhone,
                {
                  color: theme.colors.textSecondary,
                  textAlign: isRTL ? 'right' : 'left',
                  fontSize: 12,
                },
              ]}
            >
              {orgName}
            </Text>
          </View>
        </View>

        <View style={[styles.cardFooter, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
          <View style={[styles.roleBadge, { backgroundColor: getRoleColor(userRole) + '20' }]}>
            <Text style={[styles.roleText, { color: getRoleColor(userRole) }]}>
              {getRoleLabel(userRole)}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  if (!isAdmin || !hasPermission) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}>
        <Ionicons name="lock-closed" size={64} color={theme.colors.textSecondary} />
        <Text
          style={[styles.noAccessText, { color: theme.colors.text, marginTop: 16, fontSize: 18 }]}
        >
          {isArabic ? 'صلاحية مطلوبة' : 'Permission Required'}
        </Text>
        <Text style={[styles.noAccessSubtext, { color: theme.colors.textSecondary, marginTop: 8 }]}>
          {isArabic
            ? 'يجب أن تكون مدير منظمة للوصول لهذه الصفحة'
            : 'Organization admin access required'}
        </Text>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
          {t('common.loading')}
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <FlatList
        data={users}
        renderItem={renderUserCard}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[theme.colors.primary]}
            tintColor={theme.colors.primary}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
              {t('users.noUsers')}
            </Text>
          </View>
        }
      />

      <TouchableOpacity
        style={[styles.fab, { backgroundColor: theme.colors.primary }]}
        onPress={handleAdd}
        accessible={true}
        accessibilityLabel={isArabic ? 'إضافة مستخدم جديد' : 'Add new user'}
        accessibilityHint={
          isArabic
            ? 'انقر نقرًا مزدوجًا لفتح نموذج إضافة مستخدم جديد'
            : 'Double tap to open form to add a new user'
        }
        accessibilityRole="button"
      >
        <Text style={styles.fabIcon}>+</Text>
      </TouchableOpacity>

      <Modal
        visible={modalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.surface }]}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <Text
                style={[
                  styles.modalTitle,
                  { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                ]}
              >
                {editingUser ? t('users.editUser') : t('users.addUser')}
              </Text>

              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                    borderColor: theme.colors.border,
                    textAlign: isRTL ? 'right' : 'left',
                  },
                ]}
                placeholder={isArabic ? 'الاسم الأول' : 'First Name'}
                placeholderTextColor={theme.colors.placeholder}
                value={formData.firstName}
                onChangeText={(text) => setFormData({ ...formData, firstName: text })}
                accessible={true}
                accessibilityLabel={isArabic ? 'الاسم الأول (مطلوب)' : 'First name (required)'}
                accessibilityHint={isArabic ? 'أدخل الاسم الأول للمستخدم' : 'Enter user first name'}
              />

              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                    borderColor: theme.colors.border,
                    textAlign: isRTL ? 'right' : 'left',
                  },
                ]}
                placeholder={isArabic ? 'الاسم الأخير' : 'Last Name'}
                placeholderTextColor={theme.colors.placeholder}
                value={formData.lastName}
                onChangeText={(text) => setFormData({ ...formData, lastName: text })}
                accessible={true}
                accessibilityLabel={isArabic ? 'الاسم الأخير (مطلوب)' : 'Last name (required)'}
                accessibilityHint={isArabic ? 'أدخل الاسم الأخير للمستخدم' : 'Enter user last name'}
              />

              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                    borderColor: theme.colors.border,
                    textAlign: isRTL ? 'right' : 'left',
                  },
                ]}
                placeholder={t('users.email')}
                placeholderTextColor={theme.colors.placeholder}
                value={formData.email}
                onChangeText={(text) => setFormData({ ...formData, email: text })}
                keyboardType="email-address"
                autoCapitalize="none"
                accessible={true}
                accessibilityLabel={
                  isArabic ? 'البريد الإلكتروني (مطلوب)' : 'Email address (required)'
                }
                accessibilityHint={
                  isArabic
                    ? 'أدخل عنوان بريد إلكتروني صالح للمستخدم'
                    : 'Enter valid email address for user'
                }
              />

              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                    borderColor: theme.colors.border,
                    textAlign: isRTL ? 'right' : 'left',
                  },
                ]}
                placeholder={
                  editingUser
                    ? isArabic
                      ? 'كلمة المرور (اتركها فارغة لعدم التغيير)'
                      : 'Password (leave empty to keep unchanged)'
                    : isArabic
                    ? 'كلمة المرور'
                    : 'Password'
                }
                placeholderTextColor={theme.colors.placeholder}
                value={formData.password}
                onChangeText={(text) => setFormData({ ...formData, password: text })}
                secureTextEntry
                autoCapitalize="none"
                accessible={true}
                accessibilityLabel={
                  editingUser
                    ? isArabic
                      ? 'كلمة المرور (اختياري للتعديل)'
                      : 'Password (optional for edit)'
                    : isArabic
                    ? 'كلمة المرور (مطلوب)'
                    : 'Password (required)'
                }
                accessibilityHint={
                  editingUser
                    ? isArabic
                      ? 'اتركها فارغة للاحتفاظ بكلمة المرور الحالية'
                      : 'Leave empty to keep current password'
                    : isArabic
                    ? 'أدخل كلمة مرور آمنة للمستخدم'
                    : 'Enter secure password for user'
                }
              />

              <View
                style={[
                  styles.pickerContainer,
                  {
                    backgroundColor: theme.colors.background,
                    borderColor: theme.colors.border,
                  },
                ]}
                accessible={true}
                accessibilityLabel={`${isArabic ? 'الدور' : 'Role'}: ${
                  availableRoles.find((r) => r.id === formData.role)?.name || ''
                }`}
                accessibilityRole="button"
                accessibilityHint={
                  isArabic ? 'حدد دور المستخدم في النظام' : 'Select user role in the system'
                }
              >
                <Picker
                  selectedValue={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  style={{
                    color: theme.colors.text,
                    backgroundColor: theme.colors.background,
                  }}
                  dropdownIconColor={theme.colors.text}
                  itemStyle={{
                    color: theme.colors.text,
                    backgroundColor: theme.colors.background,
                  }}
                >
                  {availableRoles
                    .filter((r) => r.scope !== 'platform')
                    .map((r) => (
                      <Picker.Item
                        key={r.id}
                        label={isArabic ? r.nameAr || r.name : r.name}
                        value={r.id}
                        color={theme.colors.text}
                      />
                    ))}
                  {availableRoles.filter((r) => r.scope !== 'platform').length === 0 && (
                    <Picker.Item
                      label={isArabic ? 'جارٍ التحميل...' : 'Loading...'}
                      value=""
                      color={theme.colors.placeholder}
                    />
                  )}
                </Picker>
              </View>

              <View style={[styles.modalButtons, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                <TouchableOpacity
                  style={[
                    styles.button,
                    styles.cancelButton,
                    { backgroundColor: theme.colors.disabled },
                  ]}
                  onPress={() => setModalVisible(false)}
                  accessible={true}
                  accessibilityLabel={isArabic ? 'إلغاء' : 'Cancel'}
                  accessibilityHint={
                    isArabic
                      ? 'انقر نقرًا مزدوجًا لإغلاق النموذج بدون حفظ'
                      : 'Double tap to close form without saving'
                  }
                  accessibilityRole="button"
                >
                  <Text style={[styles.buttonText, { color: theme.colors.text }]}>
                    {t('common.cancel')}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.button,
                    styles.saveButton,
                    { backgroundColor: theme.colors.primary },
                  ]}
                  onPress={handleSave}
                  accessible={true}
                  accessibilityLabel={
                    editingUser
                      ? isArabic
                        ? 'تحديث المستخدم'
                        : 'Update user'
                      : isArabic
                      ? 'إضافة مستخدم'
                      : 'Add user'
                  }
                  accessibilityHint={
                    editingUser
                      ? isArabic
                        ? 'انقر نقرًا مزدوجًا لحفظ تغييرات المستخدم'
                        : 'Double tap to save user changes'
                      : isArabic
                      ? 'انقر نقرًا مزدوجًا لإنشاء مستخدم جديد'
                      : 'Double tap to create new user'
                  }
                  accessibilityRole="button"
                >
                  <Text style={[styles.buttonText, { color: '#ffffff' }]}>{t('common.save')}</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noAccessText: {
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 16,
  },
  noAccessSubtext: {
    fontSize: 14,
    marginTop: 8,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
  listContent: {
    padding: 16,
  },
  card: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    // Web-compatible boxShadow
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  avatarText: {
    color: '#ffffff',
    fontSize: 20,
    fontWeight: 'bold',
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  userEmail: {
    fontSize: 14,
    marginBottom: 2,
  },
  userPhone: {
    fontSize: 12,
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roleBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  roleText: {
    fontSize: 12,
    fontWeight: '600',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
    gap: 4,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
  },
  fab: {
    position: 'absolute',
    right: 16,
    bottom: 16,
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    // Web-compatible boxShadow
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.3)',
  },
  fabIcon: {
    color: '#ffffff',
    fontSize: 32,
    fontWeight: '300',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '90%',
    borderRadius: 16,
    padding: 20,
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
  },
  input: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    fontSize: 16,
  },
  pickerContainer: {
    borderRadius: 8,
    marginBottom: 12,
    borderWidth: 1,
    overflow: 'hidden',
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    marginBottom: 12,
  },
  switchLabel: {
    fontSize: 16,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    gap: 12,
  },
  button: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {},
  saveButton: {},
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});
